import { Controller } from "@hotwired/stimulus";

/**
 * 商品詳細モーダルの画像カルーセルを管理するStimulusコントローラー
 *
 * 機能:
 * - 画像のスライド表示（translateXによる切り替え）
 * - PC版：左右ボタンによる切り替え
 * - モバイル版：スワイプ操作による切り替え
 * - サムネイルクリックによる切り替え
 * - 選択中サムネイルのハイライト表示
 * - ページインジケーター（1/10形式）
 */
export default class extends Controller {
  static targets = [
    "carousel",
    "slide",
    "prevButton",
    "nextButton",
    "thumbnail",
    "indicator",
    "thumbnailContainer",
    "thumbLeftChevron",
    "thumbRightChevron"
  ];

  static values = {
    currentIndex: { type: Number, default: 0 },
    total: Number
  };

  connect() {
    this.currentIndexValue = 0;

    // スワイプ用の変数を初期化
    this.startX = 0;
    this.currentX = 0;
    this.isDragging = false;

    // イベントハンドラーをバインドして保存
    this.boundHandlers = {
      touchStart: this.handleTouchStart.bind(this),
      touchMove: this.handleTouchMove.bind(this),
      touchEnd: this.handleTouchEnd.bind(this)
    };

    if (this.totalValue > 1) {
      this.setupTouchEvents();
      this.setupThumbnailScroll();
    }

    this.updateCarousel();
    this.updateThumbnails();
    this.updateIndicator();
    this.updateThumbnailChevrons();
  }

  disconnect() {
    this.removeTouchEvents();
    this.removeThumbnailScrollEvents();
  }

  setupTouchEvents() {
    const carousel = this.carouselTarget;

    // タッチイベント（モバイル用スワイプ）
    carousel.addEventListener("touchstart", this.boundHandlers.touchStart, {
      passive: true
    });
    carousel.addEventListener("touchmove", this.boundHandlers.touchMove, {
      passive: false
    });
    carousel.addEventListener("touchend", this.boundHandlers.touchEnd, {
      passive: true
    });
  }

  removeTouchEvents() {
    if (!this.boundHandlers || !this.hasCarouselTarget) return;

    const carousel = this.carouselTarget;
    carousel.removeEventListener("touchstart", this.boundHandlers.touchStart);
    carousel.removeEventListener("touchmove", this.boundHandlers.touchMove);
    carousel.removeEventListener("touchend", this.boundHandlers.touchEnd);
  }

  setupThumbnailScroll() {
    if (!this.hasThumbnailContainerTarget) return;

    this.boundThumbnailScrollHandler = this.updateThumbnailChevrons.bind(this);
    this.thumbnailContainerTarget.addEventListener(
      "scroll",
      this.boundThumbnailScrollHandler,
      { passive: true }
    );
  }

  removeThumbnailScrollEvents() {
    if (!this.boundThumbnailScrollHandler || !this.hasThumbnailContainerTarget)
      return;

    this.thumbnailContainerTarget.removeEventListener(
      "scroll",
      this.boundThumbnailScrollHandler
    );
  }

  scrollThumbnailsLeft() {
    if (!this.hasThumbnailContainerTarget) return;

    const container = this.thumbnailContainerTarget;
    const scrollAmount = 72; // サムネイル幅(64px) + gap(8px)
    container.scrollBy({ left: -scrollAmount, behavior: "smooth" });
  }

  scrollThumbnailsRight() {
    if (!this.hasThumbnailContainerTarget) return;

    const container = this.thumbnailContainerTarget;
    const scrollAmount = 72; // サムネイル幅(64px) + gap(8px)
    container.scrollBy({ left: scrollAmount, behavior: "smooth" });
  }

  updateThumbnailChevrons() {
    if (!this.hasThumbnailContainerTarget) return;

    const container = this.thumbnailContainerTarget;
    const scrollLeft = container.scrollLeft;
    const maxScroll = container.scrollWidth - container.clientWidth;

    // 左矢印: スクロール可能なら表示
    if (this.hasThumbLeftChevronTarget) {
      this.thumbLeftChevronTarget.classList.toggle("opacity-0", scrollLeft <= 0);
      this.thumbLeftChevronTarget.classList.toggle("opacity-100", scrollLeft > 0);
    }

    // 右矢印: スクロール可能なら表示
    if (this.hasThumbRightChevronTarget) {
      this.thumbRightChevronTarget.classList.toggle(
        "opacity-0",
        scrollLeft >= maxScroll - 1
      );
      this.thumbRightChevronTarget.classList.toggle(
        "opacity-100",
        scrollLeft < maxScroll - 1
      );
    }
  }

  scrollThumbnailIntoView() {
    if (!this.hasThumbnailContainerTarget || !this.hasThumbnailTarget) return;

    const container = this.thumbnailContainerTarget;
    const thumbnail = this.thumbnailTargets[this.currentIndexValue];
    if (!thumbnail) return;

    const containerRect = container.getBoundingClientRect();
    const thumbnailRect = thumbnail.getBoundingClientRect();

    // サムネイルがコンテナの表示範囲外にある場合、スクロールして表示
    if (thumbnailRect.left < containerRect.left) {
      container.scrollBy({
        left: thumbnailRect.left - containerRect.left - 8,
        behavior: "smooth"
      });
    } else if (thumbnailRect.right > containerRect.right) {
      container.scrollBy({
        left: thumbnailRect.right - containerRect.right + 8,
        behavior: "smooth"
      });
    }
  }

  previous() {
    if (this.currentIndexValue <= 0) {
      this.currentIndexValue = this.totalValue - 1;
    } else {
      this.currentIndexValue--;
    }
    this.updateCarousel();
    this.updateThumbnails();
    this.updateIndicator();
    this.scrollThumbnailIntoView();
  }

  next() {
    if (this.currentIndexValue >= this.totalValue - 1) {
      this.currentIndexValue = 0;
    } else {
      this.currentIndexValue++;
    }
    this.updateCarousel();
    this.updateThumbnails();
    this.updateIndicator();
    this.scrollThumbnailIntoView();
  }

  goToSlide(event) {
    // ボタン内の画像がクリックされた場合もボタン要素からdata-indexを取得
    const button =
      event.currentTarget.closest("[data-index]") || event.currentTarget;
    const index = parseInt(button.dataset.index, 10);
    if (isNaN(index)) return;

    this.currentIndexValue = index;
    this.updateCarousel();
    this.updateThumbnails();
    this.updateIndicator();
    this.scrollThumbnailIntoView();
  }

  updateCarousel() {
    const translateX = -this.currentIndexValue * 100;
    this.carouselTarget.style.transform = `translateX(${translateX}%)`;
  }

  updateThumbnails() {
    if (!this.hasThumbnailTarget) return;

    this.thumbnailTargets.forEach((thumbnail, index) => {
      // まずすべての状態クラスを削除
      thumbnail.classList.remove("outline-primary", "outline-transparent", "hover:outline-gray-300");

      if (index === this.currentIndexValue) {
        thumbnail.classList.add("outline-primary");
      } else {
        thumbnail.classList.add("outline-transparent", "hover:outline-gray-300");
      }
    });
  }

  updateIndicator() {
    if (!this.hasIndicatorTarget) return;

    this.indicatorTarget.textContent = `${this.currentIndexValue + 1}/${this.totalValue}`;
  }

  // タッチイベントハンドラー
  handleTouchStart(event) {
    this.startX = event.touches[0].clientX;
    this.isDragging = true;
  }

  handleTouchMove(event) {
    if (!this.isDragging) return;

    this.currentX = event.touches[0].clientX;
    // 横スワイプを検出したらスクロールを防ぐ
    if (Math.abs(this.currentX - this.startX) > 10) {
      event.preventDefault();
    }
  }

  handleTouchEnd() {
    if (!this.isDragging) return;

    const diffX = this.startX - this.currentX;
    const threshold = 50; // スワイプと判定する最小距離

    if (Math.abs(diffX) > threshold) {
      if (diffX > 0) {
        // 左スワイプ（次へ）
        this.next();
      } else {
        // 右スワイプ（前へ）
        this.previous();
      }
    }

    this.isDragging = false;
  }
}
